import React, {useEffect, useState} from "react";
import {GridColDef, GridPaginationModel} from '@mui/x-data-grid-pro';
import {useLazyQuery, useMutation} from "@apollo/client";
import _ from "lodash";
import {ActionsMenu, getInitialPagination} from "@dreebit-registry/vsm-microservice-components";
import {resolveErrorMessage} from "../../utils/resolveErrorMessage";
import {paginationLimit} from "../../config/paginationLimit";
import {Typography} from "@mui/joy";
import Table from "../Table";
import {InstalledBaseNodeAttributeList} from "../../graphql/queries/installedBaseNodeAttributeList";
import {toast} from "react-toastify";
import {DeleteRounded} from "@mui-symbols-material/w300";
import {useTranslation} from "react-i18next";
import {DeleteInstalledBaseNodeAttribute} from "../../graphql/mutations/deleteInstalledBaseNodeAttribute";
import ConfirmDeleteModal from "../ConfirmDeleteModal";


interface Props {
    height: number,
    onChangePagination?: (newPaginationModel: GridPaginationModel) => void,
    searchParams?: { query: string, fields: string[] }
    urlParams?: URLSearchParams,
    filter?: any
    onOpenAttribute: (attributeId: string) => void;
}

const AttributesTable = ({
                                height,
                                onChangePagination,
                                searchParams,
                                urlParams,
                                filter,
                                onOpenAttribute,
                            }: Props) => {
    const {t} = useTranslation();


    const [paginationModel, setPaginationModel] = useState(getInitialPagination(paginationLimit, urlParams));

    useEffect(() => {
        setPaginationModel({
            page: getInitialPagination(paginationLimit, urlParams).page,
            pageSize: getInitialPagination(paginationLimit, urlParams).pageSize
        })
    }, [searchParams])

    useEffect(() => {
        if (onChangePagination) {
            onChangePagination(paginationModel)
        }
    }, [paginationModel])

    const [deleteAttributeMutation, deleteAttributeResult] = useMutation(DeleteInstalledBaseNodeAttribute);
    const [deleteTarget, setDeleteTarget] = useState<{ id: string, name: string } | null>(null);

    const [fetchAttributeList, attributeListQuery] = useLazyQuery(InstalledBaseNodeAttributeList, {
        variables: {
            search: searchParams ? {
                query: searchParams.query,
                fields: searchParams.fields
            } : {},
            pagination: {
                start: paginationModel.page * paginationModel.pageSize,
                limit: paginationModel.pageSize
            }
        },
        fetchPolicy: "network-only"
    });
    const attributeListData = _.get(attributeListQuery, 'data.installedBaseNodePropertyDefinitionList.installedBaseNodePropertyDefinitions', []);
    const totalAttributes = _.get(attributeListQuery, 'data.installedBaseNodePropertyDefinitionList.total')

    useEffect(() => {
        fetchAttributeList();
    }, [fetchAttributeList]);

    const onDeleteAttribute = () => {
        if (!deleteTarget) return;
        deleteAttributeMutation({
            variables: {
                id: deleteTarget.id,
            },
            refetchQueries: ["InstalledBaseNodeAttributeList"]
        })
            .then((res) => {
                if (res.data?.deleteInstalledBaseNodePropertyDefinition?.success && res.data?.deleteInstalledBaseNodePropertyDefinition?.message) {
                    toast.success(res.data.deleteInstalledBaseNodePropertyDefinition.message);
                }
                setDeleteTarget(null);
            })
            .catch((err) => {
                toast.error(resolveErrorMessage(err, t))
                setDeleteTarget(null);
            })
    }

    const columns:GridColDef[] = [
        {
            field: 'preferredName',
            headerName: t('Name'),
            flex: 1,
            renderCell: (params) => {
                const attributeId = _.get(params.row, 'id')
                const attributeName = _.get(params.row, 'preferredName')

                return <Typography fontWeight={"bold"} sx={{cursor: 'pointer'}} color={"primary"}
                                   onClick={() => onOpenAttribute(attributeId)}>
                    {attributeName}
                </Typography>;
            },
            display: "flex",
            disableColumnMenu: true

        },
        {
            field: 'shortName',
            headerName: t('Short Name'),
            flex: 1,
            disableColumnMenu: true,
        },
        {
            field: 'alias',
            headerName: t('Alias'),
            flex: 1,
            disableColumnMenu: true,
        },
        {
            field: 'dataType',
            headerName: t('Data Type'),
            flex: 1,
            disableColumnMenu: true,
        },
        {
            field: 'inputType',
            headerName: t('Input Type'),
            flex: 1,
            disableColumnMenu: true,
        },
        {
            field: 'actions',
            headerName: t('Actions'),
            width: 80,
            disableColumnMenu: true,
            align: "center",
            renderCell: (params) => {
                const attributeId = _.get(params, 'row.id')
                const attributeName = _.get(params, 'row.preferredName')

                const actionItems: { item: React.ReactNode, onClick: () => void, active: boolean }[] = [
                    {
                        item: <><DeleteRounded fontSize={'inherit'} sx={{mr: '4px'}}/>{t('Delete')}</>,
                        onClick: () => setDeleteTarget({id: attributeId, name: attributeName}),
                        active: true,
                    },
                ]

                return <ActionsMenu items={actionItems} />
            }
        }
    ]

    return (
        <div style={{width: "100%", height: height}}>
            <Table
                loading={attributeListQuery.loading}
                data={attributeListData}
                rowCount={totalAttributes}
                columns={columns}
                pagination={true}
                paginationMode="server"
                paginationModel={paginationModel}
                pageSizeOptions={[paginationLimit]}
                onPaginationModelChange={setPaginationModel}
                disableColumnFilter
                disableColumnSorting
                disableRowSelectionOnClick
                disableColumnResize
                disableColumnReorder
                disableColumnPinning
            />
            {deleteTarget && (
                <ConfirmDeleteModal
                    open={!!deleteTarget}
                    title={t('Delete attribute')}
                    message={t('Are you sure you want to delete the attribute {{name}}? This action cannot be undone.', {name: deleteTarget.name})}
                    loading={deleteAttributeResult.loading}
                    onConfirm={onDeleteAttribute}
                    onClose={() => setDeleteTarget(null)}
                />
            )}
        </div>
    );
}

export default AttributesTable;
